import { memo, useCallback } from 'react';
import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { AngleIcon } from '@/assets/icons';
import { NumberInput } from '@common/components/inputs';

type Props = {
  x: number;
  y: number;
  length: number;
  width: number;
  angle?: number;
  disableLength?: boolean;
  disableWidth?: boolean;
  disableAngle?: boolean;
  disabled?: boolean;
  onXChange?: (value: number) => void;
  onYChange?: (value: number) => void;
  onLengthChange?: (value: number) => void;
  onWidthChange?: (value: number) => void;
  onAngleChange?: (value: number) => void;
  onAngleBlur?: (value: number) => void;
  onXBlur?: (value: number) => void;
  onYBlur?: (value: number) => void;
  onLengthBlur?: (value: number) => void;
  onWidthBlur?: (value: number) => void;
  onFocus?: () => void;
};

const createTextSx = (disabled = false) => ({
  opacity: disabled ? 0.5 : 1,
});

function DimensionsComponent({
  x,
  y,
  length,
  disableLength = false,
  width,
  disableWidth = false,
  angle,
  disableAngle = false,
  disabled = false,
  onXChange,
  onYChange,
  onLengthChange,
  onWidthChange,
  onAngleChange,
  onXBlur,
  onYBlur,
  onLengthBlur,
  onWidthBlur,
  onAngleBlur,
  onFocus,
}: Props) {
  const unit = 'mm';
  const { t } = useTranslation();
  const unitLabel = t(`interface:settings.units.${unit}`, unit);

  const lengthIsDisabled = disabled || disableLength;
  const widthIsDisabled = disabled || disableWidth;
  const angleIsDisabled = disabled || disableAngle;

  const handleXChange = useCallback(
    (value: number) => {
      onXChange?.(value);
    },
    [onXChange],
  );

  const handleYChange = useCallback(
    (value: number) => {
      onYChange?.(value);
    },
    [onYChange],
  );

  const handleLengthChange = useCallback(
    (value: number) => {
      onLengthChange?.(value);
    },
    [onLengthChange],
  );

  const handleWidthChange = useCallback(
    (value: number) => {
      onWidthChange?.(value);
    },
    [onWidthChange],
  );

  const handleAngleChange = useCallback(
    (value: number) => {
      onAngleChange?.(value);
    },
    [onAngleChange],
  );

  return (
    <Grid
      container
      alignItems='center'
      justifyContent='space-between'
      rowGap={1}
      columns={36}
      sx={{ '&:hover': { cursor: 'default' } }}
    >
      <Grid item xs={3}>
        <Typography fontSize='10px' sx={createTextSx(disabled)}>
          X
        </Typography>
      </Grid>

      <Grid item xs={14}>
        <NumberInput
          value={x}
          unit={unitLabel}
          errorType='floating'
          disabled={disabled}
          onChange={handleXChange}
          onBlur={onXBlur}
          onFocus={onFocus}
        />
      </Grid>

      <Grid item xs={2}>
        {/* Intentionally Empty */}
      </Grid>

      <Grid item xs={3} justifyContent='center'>
        <Typography fontSize='10px' sx={createTextSx(disabled)}>
          Y
        </Typography>
      </Grid>

      <Grid item xs={14}>
        <NumberInput
          value={y}
          unit={unitLabel}
          disabled={disabled}
          errorType='floating'
          onChange={handleYChange}
          onBlur={onYBlur}
          onFocus={onFocus}
        />
      </Grid>

      <Grid item xs={3}>
        <Typography fontSize='10px' sx={createTextSx(lengthIsDisabled)}>
          L
        </Typography>
      </Grid>

      <Grid item xs={14}>
        <NumberInput
          value={length}
          unit={unitLabel}
          errorType='floating'
          disabled={lengthIsDisabled}
          minValue={0}
          onChange={handleLengthChange}
          onBlur={onLengthBlur}
          onFocus={onFocus}
          onChangeBlur
          debounceMs={1000}
        />
      </Grid>

      <Grid item xs={2}>
        {/* Intentionally Empty */}
      </Grid>

      <Grid item xs={3}>
        <Typography fontSize='10px' sx={createTextSx(widthIsDisabled)}>
          W
        </Typography>
      </Grid>

      <Grid item xs={14}>
        <NumberInput
          value={width}
          unit={unitLabel}
          errorType='floating'
          disabled={widthIsDisabled}
          minValue={0}
          onChange={handleWidthChange}
          onBlur={onWidthBlur}
          onFocus={onFocus}
          onChangeBlur
          debounceMs={1000}
        />
      </Grid>

      <Grid item xs={3} sx={createTextSx(angleIsDisabled)} columnGap={2}>
        <AngleIcon style={{ height: '16px', width: '16px' }} />
      </Grid>

      <Grid item xs={14} sx={{ marginRight: 'auto' }} columnGap={1}>
        <NumberInput
          value={angle ?? 0}
          unit='°'
          disabled={angleIsDisabled}
          errorType='floating'
          onChange={handleAngleChange}
          onBlur={onAngleBlur}
          onFocus={onFocus}
        />
      </Grid>
    </Grid>
  );
}

export const Dimensions = memo(DimensionsComponent);
